/* eslint-disable react-hooks/exhaustive-deps */
import Cookies from "js-cookie";
import { lazy, Suspense, useContext, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { getUserDetailsApi } from "../api/user/getUserDetails";
import { ROUTES } from "../constant";
import { ENVIRONMENT_TYPE } from "../constant/common";
import { WEB_URL, environment } from "../constant/environment";
import { StateContext } from "../context/globalContext/context";
import { iGlobalContext } from "../context/globalContext/interface";
import { setItemInLocalStorage } from "../hooks/localStorage";
import { isValidJWT } from "../utils/authentication/isValidJWT";
import { isAllowedPartnerOrigin, isEmbedMode } from "../utils/embedMode";
import TripHelp from "./tripHelp/TripHelp";
// OnBoarding is the landing route for most unauthenticated users; keeping it
// eager avoids a lazy-chunk round-trip on the LCP critical path.
import OnBoarding from "./onBoarding/onBoarding";
import LoadingDots, { SHOW_LOADING_DOTS }  from "../component/reusableComponent/loadingDots/LoadingDots";
import MobileScrollContainer from "../component/reusableComponent/mobileScrollContainer/MobileScrollContainer";
import BookingFormSkeleton from "../component/BookingInProgress/component/bookingFormSkeleton/BookingFormSkeleton";
import DelayedFallback from "../component/reusableComponent/delayedFallback/DelayedFallback";
import ChatHomeSkeleton from "../component/TripManagement/newTripPage/components/ChatHomeSkeleton";
import MobileAccount from "./account/MobileAccount";

// Code splitting: Lazy load route components using dynamic import()
const NotFound = lazy(() => import("../component/NotFound/NotFound"));
const AboutUs = lazy(() => import("../component/aboutUs/aboutUs"));
const DesktopLandingPage = lazy(() => import("../component/home&Landingpage/desktopLandingPage/DesktopLandingPage"));
const ExploreTripAndAssistance = lazy(() => import("./exploreTripAndAssistance/ExploreTripAndAssistance"));
const FaqAssistance = lazy(() => import("./faqAssistance/FaqAssistance"));
const HotelDetails = lazy(() => import("../component/reusableComponent/hotelCard/components/hotelDetails"));
const MyTrips = lazy(() => import("./myTripsPage/myTrips"));
const RecommendedTripDetailsPage = lazy(
  () => import("../component/recommendedTripDetailsPage/RecommendedTripDetailsPage")
);
const TripDetailsPage = lazy(() => import("./tripDetailsPage/TripDetailsPage"));
const EmailVerificationMessage = lazy(() => import("../component/verifyEmail/EmailVerificationMessage"));
const TodayTrip = lazy(() => import("../routes/todayTrip/todayTrip"));
const TravelerInformation = lazy(() => import("../routes/travelerInformation/travellerInformation"));
const AdjustTrip = lazy(() => import("./adjustTrip/adjustTrip"));
const BookingInProgress = lazy(() => import("./bookingInProgress/BookingInProgress"));
const BookingLoader = lazy(() => import("../component/BookingInProgress/component/bookingLoader/BookingLoader"));
const EditAccount = lazy(() => import("./editAccount/EditAccount"));
const FaqAccordion = lazy(() => import("./faqsAccordion/faqsAccordion"));
const FlightDelay = lazy(() => import("./flightDelay/flightDelay"));
const FlightSelect = lazy(() => import("./flightSelect/flightSelect"));
const ItineraryDetails = lazy(() => import("./itineraryDetails/ItineraryDetails"));
const ModifyFlights = lazy(() => import("./modifyFlights/modifyFlights"));
const ModifyHotelReservation = lazy(() => import("./modifyHotelReservation/modifyHotelReservation"));
const ModifyHotelsSuccess = lazy(() => import("./modifyHotelsSuccess/modifyHotelsSuccess"));
const NewTrip = lazy(() => import("./newTrip/newTrip"));
const PaymentFailure = lazy(() => import("./payment-failure"));
const StripeSuccess = lazy(() => import("./payment-success"));
const PreviewPreference = lazy(() => import("./previewPreference/previewPreference"));
const PrivacyPolicy = lazy(() => import("./privacy-policy/privacy-policy"));
const ProtectedRoute = lazy(() => import("./protectedRoute/ProtectedRoute"));
const RecommendedTrips = lazy(() => import("./recommendedTrips/RecommendedTrips"));
const ReorderMultiCityTrip = lazy(() => import("./reorderMultipCityTrip/ReorderMultiCityTrip"));
const ResetPasswordPage = lazy(() => import("./resetPassword/ResetPasswordPage"));
const SavedItineraryDetail = lazy(() => import("./savedItineraryDetails/SavedItineraryDetail"));
const TermsOfService = lazy(() => import("./termsOfService/termsOfService"));
const Test = lazy(() => import("./testPage/Test"));
const TopDestination = lazy(() => import("./topDestination/TopDestination"));
const ViewShareItinerary = lazy(() => import("./viewSharedItineraryDetails/ViewShareItinerary"));
const TripsById = lazy(() => import("./tripsByItinerary/TripsById"));
const WeatherAdvisory = lazy(() => import("./weatherAdvisory/weatherAdvisory"));
const TripDetailsRedirect = lazy(() => import("./redirectRoutes/TripDetailsRedirect"));
const VerifyEmailRedirect = lazy(() => import("./redirectRoutes/VerifyEmailRedirect"));
const SavedTrip = lazy(() => import("./savedTrip/SavedTrip"));
const TicketDetails = lazy(() => import("./ticketDetails/TicketDetails"));
const HistoryPage = lazy(() => import("./historyPage/HistoryPage"));
const Preferences = lazy(() => import("./preferences/preferences"));
const GoogleOAuth = lazy(() => import("./googleOAuth/GoogleOAuth"));

export default function AppRoutes() {
  // Master switch for the Suspense fallback indicator. Flip to `false` to disable the centered "Loading" + wavy dots entirely — the component renders nothing and the Suspense fallback effectively becomes empty (purple body shows through during lazy chunk loads, the pre-fix behavior). Co-located with the component so toggling is one edit.
// const SHOW_LOADING_DOTS = true;
  const context = useContext(StateContext);
  const { userInfo, setUserInfo, accessJWT, isMobile }: iGlobalContext = context;

  async function getUserDetails(userId: string) {
    const response = await getUserDetailsApi(accessJWT, userId);
    if (response) {
      setUserInfo((prevInfo) => ({
        ...prevInfo,
        id: response.id,
        name: response.name,
        email: response.email,
        phone: response.phone,
        profile_pic: response.profile_pic,
        loggedIn: true,
        role: response?.role || 0,
      }));
    }
    if (!response) {
      return;
    }
  }

  useEffect(() => {
    if (window && typeof window !== "undefined") {
      window?.addEventListener("message", (event) => {
        // Same-origin marketing iframe (`zenvoya.ai` → `chat.zenvoya.ai`) is the
        // primary SSO sender. In third-party embed mode, additionally accept
        // payloads from origins on the partner allowlist (ships empty; populate
        // per-partner once SSO integration is signed off).
        const sameOrigin = `${event.origin}/` === WEB_URL;
        const partnerOriginOk = isEmbedMode() && isAllowedPartnerOrigin(event.origin);
        if (!sameOrigin && !partnerOriginOk) {
          return;
        }
        const data = JSON.parse(event.data);
        if (typeof data?.id !== "undefined" && data?.loggedIn) {
          setUserInfo(data);
          getUserDetails(data.id);
          // store the JWT to local storagex
          setItemInLocalStorage("_zenvoya_JWT", data.jwt);
          return;
        }
        setUserInfo({
          id: "",
          name: "",
          email: "",
          phone: "",
          profile_pic: "",
          loggedIn: false,
          role: 0,
        });
        window.localStorage.removeItem("_zenvoya_JWT");
      });
    }
  }, [getUserDetails]);

  useEffect(() => {
    if (environment === ENVIRONMENT_TYPE.LOCAL) return;
    const userData = Cookies.get(`${environment}userData`);
    if (userData) {
      const data = JSON.parse(userData);
      if (typeof data?.id !== "undefined" && data?.loggedIn && isValidJWT(data.jwt)) {
        setUserInfo(data);
        getUserDetails(data.id);
        // store the JWT to local storage
        setItemInLocalStorage("_zenvoya_JWT", data.jwt);
        return;
      }
    }

    if (!userData) {
      setUserInfo({
        id: "",
        name: "",
        email: "",
        phone: "",
        profile_pic: "",
        loggedIn: false,
        preferences: {},
        role: 0,
      });
      setItemInLocalStorage("_zenvoya_JWT", "");
    }
  }, []);

  const chatFallback = isMobile ? (
    <DelayedFallback>
      <ChatHomeSkeleton />
    </DelayedFallback>
  ) : null;

  return (
    <MobileScrollContainer>
      <Suspense fallback={SHOW_LOADING_DOTS ? <LoadingDots showText={false} /> : null}>
      <Routes>
        <Route path={"/app/code"} element={<GoogleOAuth />} />

        <Route path={ROUTES.TripHelp} element={<TripHelp />} />
        <Route path={"/test"} element={<Test />} />
        <Route path={ROUTES.History} element={<HistoryPage />} />
        <Route path={ROUTES.BookingInProgress} element={<Suspense fallback={<DelayedFallback><BookingFormSkeleton type="stays" withHeader /></DelayedFallback>}><BookingInProgress /></Suspense>} />

        <Route path={ROUTES.BookingLoader} element={<BookingLoader />} />
        <Route path={ROUTES.ForYou} element={<RecommendedTrips />} />
        <Route path={ROUTES.Home} element={<Suspense fallback={chatFallback}><DesktopLandingPage /></Suspense>} />

        <Route path={ROUTES.ItineraryDetails} element={<ItineraryDetails />} />
        <Route
          path={ROUTES.EditAccount}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={EditAccount} />}
        />
        <Route path={ROUTES.Account} element={<MobileAccount />} />
        <Route path={ROUTES.AboutUs} element={<AboutUs />} />
        <Route path={ROUTES.ResetPassword + "/:id"} element={<ResetPasswordPage />} />
        <Route path="/shared/:id" element={<ViewShareItinerary />} />
        {/* lares alert-resolution SMS deep link. Public (NOT ProtectedRoute) —
            gates auth in-component, mirroring /trip + /shared/:id. */}
        <Route path={ROUTES.TripById} element={<TripsById />} />
        <Route path={ROUTES.TopDestination} element={<TopDestination />} />
        <Route path={ROUTES.SavedItineraryDetails} element={<SavedItineraryDetail />} />

        <Route path={ROUTES.AdjustTrip} element={<AdjustTrip />} />
        <Route
          path={ROUTES.PreviewPreference}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={PreviewPreference} />}
        />
        <Route
          path={ROUTES.preferences}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={Preferences} />}
        />
        <Route
          path={ROUTES.ModifyFlights}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={ModifyFlights} />}
        />
        <Route path={ROUTES.ReorderMultiCityTrip} element={<ReorderMultiCityTrip />} />
        <Route path={ROUTES.Faqs} element={<FaqAccordion />} />
        <Route path={ROUTES.Tos} element={<TermsOfService />} />
        <Route path={ROUTES.PrivacyPolicy} element={<PrivacyPolicy />} />
        <Route path={ROUTES.PaymentSuccess} element={<StripeSuccess />} />
        <Route path={ROUTES.PaymentFailure} element={<PaymentFailure />} />
        <Route path={ROUTES.NewTrip} element={<Suspense fallback={chatFallback}><NewTrip /></Suspense>} />
        <Route
          path={ROUTES.ModifyHotelReservation}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={ModifyHotelReservation} />}
        />
        <Route
          path={ROUTES.HotelDetails}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={HotelDetails} />}
        />
        <Route path={ROUTES.Verify} element={<EmailVerificationMessage />} />
        <Route path={ROUTES.VerifyOld} element={<VerifyEmailRedirect />} />
        <Route
          path={ROUTES.TravelerInformation}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={TravelerInformation} />}
        />
        <Route path={"/today"} element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={TodayTrip} />} />
        <Route path={ROUTES.MyTrips} element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={MyTrips} />} />
        <Route
          path={ROUTES.SavedTrip}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={SavedTrip} />}
        />
        <Route
          path={ROUTES.weatherAdvisory}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={WeatherAdvisory} />}
        />
        <Route
          path={ROUTES.FlightDelay}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={FlightDelay} />}
        />
        <Route
          path={ROUTES.FlightSelect}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={FlightSelect} />}
        />
        <Route
          path={ROUTES.ExploreCity}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={ExploreTripAndAssistance} />}
        />
        <Route
          path={ROUTES.FaqAssistance}
          element={<ProtectedRoute isLoggedIn={userInfo?.loggedIn} component={FaqAssistance} />}
        />
        <Route path={ROUTES.TripDetails} element={<TripDetailsPage />} />
        <Route path={ROUTES.TripDetailsOld} element={<TripDetailsRedirect />} />

        <Route path={ROUTES.RecommendedTripDetails} element={<RecommendedTripDetailsPage />} />
        <Route path={ROUTES.ModifyHotelsSuccess} element={<ModifyHotelsSuccess />} />
        <Route path={ROUTES.onBoarding} element={<OnBoarding />} />
        <Route path={ROUTES.TicketDetails + "/:ticketId"} element={<TicketDetails />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </MobileScrollContainer>
  );
}
